(() => {
  const STORAGE_KEY = 'imm_pending_booking_addons_v3';
  const PENDING_MAX_AGE_MS = 15 * 60 * 1000;

  // Single source of truth for every booking type and add-on.
  const SERVICES = {
    pocket: {
      path: '/products/pocket-digs',
      bookingTitle: 'book your pocket dig!',
      addons: {
        collector: {
          label: 'Additional Collectors',
          cartTitle: 'pocket dig - additional collector',
          order: 1,
          max: 3,
          priceSuffix: 'per additional collector',
          half: { id: 45959196082259, price: 150 },
          full: { id: 45959196115027, price: 300 }
        }
      }
    },

    machine: {
      path: '/products/book-your-machine-dig',
      bookingTitle: 'book your machine dig!',
      addons: {
        collector: {
          label: 'Additional Collectors',
          cartTitle: 'machine dig - additional collector',
          order: 1,
          max: 3,
          priceSuffix: 'per additional collector',
          half: { id: 45964147720275, price: 250 },
          full: { id: 45964147753043, price: 500 }
        }
      }
    },

    rent: {
      path: '/products/rent-the-mine',
      bookingTitle: 'book the mine now!',
      addons: {
        collector: {
          label: 'Additional Collectors',
          cartTitle: 'rent the mine - additional collector',
          order: 1,
          max: 10,
          priceSuffix: 'per additional collector',
          half: { id: 45964242124883, price: 100 },
          full: { id: 45964242157651, price: 150 }
        },

        excavator: {
          label: 'Excavator',
          cartTitle: 'rent the mine - excavator',
          order: 2,
          max: 2,
          priceSuffix: 'each',
          full: { id: 45964243828819, price: 1000 }
        },

        freshPocket: {
          label: 'Fresh Pocket',
          cartTitle: 'rent the mine - fresh pocket',
          order: 3,
          max: 2,
          priceSuffix: 'each',
          full: { id: 45964245499987, price: 750 }
        }
      }
    }
  };

  const quantities = Object.fromEntries(
    Object.entries(SERVICES).map(([serviceKey, service]) => [
      serviceKey,
      Object.fromEntries(
        Object.keys(service.addons).map(addonKey => [addonKey, 0])
      )
    ])
  );

  const preBookingSignatures = Object.fromEntries(
    Object.keys(SERVICES).map(serviceKey => [serviceKey, []])
  );

  const snapshotState = Object.fromEntries(
    Object.keys(SERVICES).map(serviceKey => [serviceKey, 'idle'])
  );

  // Cart quantity limits for booking products that do not use our add-on UI.
  const CART_ONLY_BOOKING_LIMITS = {
    'book your mine dig!': {
      service: 'mine',
      max: 5
    },
    'book your tailing pile dig!': {
      service: 'tailing',
      max: 10
    }
  };

  let orphanCleanupRunning = false;

  function normalizeTitle(value) {
    return String(value || '').trim().toLowerCase();
  }

  function getCurrentService() {
    return Object.entries(SERVICES).find(([, service]) =>
      window.location.pathname.includes(service.path)
    ) || null;
  }

  function itemMatchesService(item, serviceKey) {
    const service = SERVICES[serviceKey];
    if (!service) return false;

    return normalizeTitle(item.product_title) === service.bookingTitle;
  }

  function bookingSignature(item) {
    const properties = item?.properties || {};

    return [
      item?.variant_id || item?.id || '',
      properties.Date || '',
      properties.Time || ''
    ].join('|');
  }

  function getRowTitle(row) {
    return normalizeTitle(
      row.querySelector('.cart-item__name')?.textContent
    );
  }

  function rowMatchesService(row, serviceKey) {
    const service = SERVICES[serviceKey];
    if (!service) return false;

    return getRowTitle(row) === service.bookingTitle;
  }

  async function snapshotExistingBookings(serviceKey) {
    if (snapshotState[serviceKey] !== 'idle') return;

    snapshotState[serviceKey] = 'loading';

    try {
      const response = await fetch('/cart.js', {
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error(
          `Cart snapshot failed: ${response.status}`
        );
      }

      const cart = await response.json();

      preBookingSignatures[serviceKey] = cart.items
        .filter(item => itemMatchesService(item, serviceKey))
        .map(bookingSignature)
        .filter(Boolean);

      snapshotState[serviceKey] = 'ready';

    } catch (error) {
      preBookingSignatures[serviceKey] = [];
      snapshotState[serviceKey] = 'failed';

      console.warn(
        'IMM: could not snapshot existing bookings',
        error
      );
    }
  }

  function isFullDay(variant) {
    const text = variant?.textContent || '';

    return (
      text.includes('Full-Day') ||
      text.includes('Full Day')
    );
  }

  function findBookingWidget() {
    const frames = [...document.querySelectorAll('iframe')];

    for (const frame of frames) {
      try {
        const doc = frame.contentDocument;
        if (!doc) continue;

        const variant =
          doc.querySelector('[data-testid="variant-select"]');

        const bookNow = [...doc.querySelectorAll('button')]
          .find(button =>
            button.textContent.trim().toLowerCase() ===
            'book now'
          );

        if (variant && bookNow) {
          return {
            doc,
            variant,
            bookNow
          };
        }

      } catch (error) {
        // Ignore inaccessible Shopify/system iframes.
      }
    }

    return null;
  }

  function money(value) {
    return Number(value).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    });
  }

  function clampQuantity(value, max) {
    const qty = Math.floor(Number(value) || 0);

    return Math.max(
      0,
      Math.min(qty, max)
    );
  }

  function createQuantityControl(
    doc,
    serviceKey,
    addonKey,
    addon
  ) {
    const box = doc.createElement('div');

    box.className = 'imm-addon-control';
    box.dataset.addonType = addonKey;

    box.innerHTML = `
      <div style="
        font-size:16px;
        font-weight:600;
        margin-bottom:3px;
      ">
        ${addon.label}
      </div>

      <div style="
        font-size:12px;
        opacity:.7;
        margin-bottom:10px;
      ">
        Optional
      </div>

      <div style="
        display:flex;
        align-items:center;
        justify-content:center;
        gap:16px;
      ">
        <button
          type="button"
          data-action="minus"
          aria-label="Remove ${addon.label}"
          style="
            width:38px;
            height:38px;
            border:1px solid rgba(255,255,255,.55);
            background:transparent;
            color:#fff;
            font-size:22px;
            cursor:pointer;
          "
        >−</button>

        <span
          data-role="qty"
          style="
            min-width:24px;
            text-align:center;
            font-size:18px;
          "
        >0</span>

        <button
          type="button"
          data-action="plus"
          aria-label="Add ${addon.label}"
          style="
            width:38px;
            height:38px;
            border:1px solid rgba(255,255,255,.55);
            background:transparent;
            color:#fff;
            font-size:22px;
            cursor:pointer;
          "
        >+</button>
      </div>

      <div
        data-role="price"
        style="
          margin-top:9px;
          font-size:13px;
        "
      ></div>
    `;

    box.style.cssText = `
      padding:14px;
      margin:10px 0 0;
      border:1px solid rgba(255,255,255,.35);
      text-align:center;
      color:#fff;
      box-sizing:border-box;
    `;

    box
      .querySelector('[data-action="plus"]')
      .addEventListener('click', () => {
        if (
          quantities[serviceKey][addonKey] <
          addon.max
        ) {
          quantities[serviceKey][addonKey]++;

          updateAddonControls(serviceKey);
        }
      });

    box
      .querySelector('[data-action="minus"]')
      .addEventListener('click', () => {
        if (
          quantities[serviceKey][addonKey] > 0
        ) {
          quantities[serviceKey][addonKey]--;

          updateAddonControls(serviceKey);
        }
      });

    return box;
  }

  function updateAddonControls(serviceKey) {
    const current = getCurrentService();

    if (
      !current ||
      current[0] !== serviceKey
    ) {
      return;
    }

    const widget = findBookingWidget();
    if (!widget) return;

    const {
      doc,
      variant
    } = widget;

    const wrapper =
      doc.querySelector('#imm-booking-addons');

    if (!wrapper) return;

    const service = SERVICES[serviceKey];

    const duration =
      isFullDay(variant)
        ? 'full'
        : 'half';

    wrapper
      .querySelectorAll('.imm-addon-control')
      .forEach(box => {
        const addonKey =
          box.dataset.addonType;

        const addon =
          service.addons[addonKey];

        if (!addon) return;

        const details =
          addon[duration];

        // If this add-on has no variant/price for the
        // selected duration, it is not offered.
        if (!details) {
          quantities[serviceKey][addonKey] = 0;

          box.style.display = 'none';

          return;
        }

        box.style.display = '';

        const qty =
          quantities[serviceKey][addonKey] || 0;

        const qtyElement =
          box.querySelector('[data-role="qty"]');

        const priceElement =
          box.querySelector('[data-role="price"]');

        const plus =
          box.querySelector('[data-action="plus"]');

        qtyElement.textContent = qty;

        priceElement.textContent =
          `+${money(details.price)} ${addon.priceSuffix}`;

        plus.disabled =
          qty >= addon.max;

        plus.setAttribute(
          'aria-disabled',
          qty >= addon.max
            ? 'true'
            : 'false'
        );
      });
  }

  function installBookingAddons() {
    const current = getCurrentService();
    if (!current) return;

    const [
      serviceKey,
      service
    ] = current;

    snapshotExistingBookings(serviceKey);

    const widget = findBookingWidget();
    if (!widget) return;

    const {
      doc,
      bookNow
    } = widget;

    if (!bookNow.dataset.immAddonHandler) {
      bookNow.dataset.immAddonHandler = 'true';

      bookNow.addEventListener(
        'click',
        () => {
          const currentVariant =
            doc.querySelector(
              '[data-testid="variant-select"]'
            );

          const duration =
            isFullDay(currentVariant)
              ? 'full'
              : 'half';

          const state =
            quantities[serviceKey];

          const hasAnything =
            Object.values(state)
              .some(qty => qty > 0);

          if (!hasAnything) {
            sessionStorage.removeItem(
              STORAGE_KEY
            );

            return;
          }

          sessionStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({
              service: serviceKey,
              duration,
              quantities: {
                ...state
              },
              existingBookingSignatures: [
                ...preBookingSignatures[serviceKey]
              ],
              createdAt: Date.now()
            })
          );
        },
        true
      );
    }

    let wrapper =
      doc.querySelector('#imm-booking-addons');

    if (!wrapper) {
      wrapper =
        doc.createElement('div');

      wrapper.id =
        'imm-booking-addons';

      wrapper.style.cssText =
        'margin:10px 0 15px;';

      Object.entries(service.addons)
        .sort(
          ([, a], [, b]) =>
            a.order - b.order
        )
        .forEach(
          ([addonKey, addon]) => {
            wrapper.appendChild(
              createQuantityControl(
                doc,
                serviceKey,
                addonKey,
                addon
              )
            );
          }
        );

      bookNow.parentNode.insertBefore(
        wrapper,
        bookNow
      );
    }

    updateAddonControls(serviceKey);
  }

  function findNewBooking(
    cart,
    pending
  ) {
    const candidates =
      cart.items.filter(item =>
        itemMatchesService(
          item,
          pending.service
        ) &&
        item.properties?.Date
      );

    if (!candidates.length) {
      return null;
    }

    const oldSignatures =
      new Set(
        pending.existingBookingSignatures || []
      );

    // Prefer a booking signature that was not in the cart
    // before Book Now was clicked. Unlike line-item keys,
    // this signature is not affected by discount key changes.
    return (
      candidates.find(
        item =>
          !oldSignatures.has(
            bookingSignature(item)
          )
      ) ||
      candidates[candidates.length - 1]
    );
  }

  function sleep(ms) {
    return new Promise(resolve => {
      setTimeout(resolve, ms);
    });
  }

  async function processPendingAddons() {
    if (
      window.location.pathname !== '/cart'
    ) {
      return;
    }

    const raw =
      sessionStorage.getItem(
        STORAGE_KEY
      );

    if (!raw) return;

    let pending;

    try {
      pending =
        JSON.parse(raw);

    } catch (error) {
      sessionStorage.removeItem(
        STORAGE_KEY
      );

      console.error(
        'IMM: invalid add-on data',
        error
      );

      return;
    }

    if (
      !pending.createdAt ||
      Date.now() - pending.createdAt >
        PENDING_MAX_AGE_MS
    ) {
      sessionStorage.removeItem(
        STORAGE_KEY
      );

      console.warn(
        'IMM: expired pending add-on data cleared'
      );

      return;
    }

    const service =
      SERVICES[pending.service];

    if (
      !service ||
      !['half', 'full'].includes(
        pending.duration
      )
    ) {
      sessionStorage.removeItem(
        STORAGE_KEY
      );

      console.error(
        'IMM: incomplete add-on data',
        pending
      );

      return;
    }

    try {
      let booking = null;
      let cart = null;

      // BTA redirects quickly after booking. Give Shopify a few
      // seconds to expose the new booking in /cart.js before
      // deciding that the add-on handoff failed.
      for (let attempt = 0; attempt < 12; attempt++) {
        const response =
          await fetch(
            '/cart.js',
            {
              cache: 'no-store'
            }
          );

        if (!response.ok) {
          throw new Error(
            `Cart read failed: ${response.status}`
          );
        }

        cart =
          await response.json();

        booking =
          findNewBooking(
            cart,
            pending
          );

        if (booking) {
          break;
        }

        await sleep(350);
      }

      if (!booking) {
        console.error(
          'IMM: matching booking not found in cart after retries'
        );

        // Keep the pending data so a manual refresh can retry
        // until it expires instead of silently losing add-ons.
        return;
      }

      const date =
        booking.properties.Date;

      const items = [];

      const pendingQuantities =
        pending.quantities || {};

      Object.entries(
        service.addons
      ).forEach(
        ([addonKey, addon]) => {
          const details =
            addon[pending.duration];

          if (!details) return;

          const qty =
            clampQuantity(
              pendingQuantities[
                addonKey
              ],
              addon.max
            );

          if (qty < 1) return;

          const properties = {
            'Add-on for': date,
            _imm_service: pending.service,
            _imm_parent_date: date
          };

          if (
            booking.properties?._addons_uuid
          ) {
            properties._imm_parent_uuid =
              booking.properties._addons_uuid;
          }

          items.push({
            id: details.id,
            quantity: qty,
            properties
          });
        }
      );

      if (!items.length) {
        sessionStorage.removeItem(
          STORAGE_KEY
        );

        return;
      }

      const addResponse =
        await fetch(
          '/cart/add.js',
          {
            method: 'POST',

            headers: {
              'Content-Type':
                'application/json',

              'Accept':
                'application/json'
            },

            body:
              JSON.stringify({
                items
              })
          }
        );

      if (!addResponse.ok) {
        console.error(
          'IMM: add-on add failed',
          addResponse.status,
          await addResponse.text()
        );

        return;
      }

      sessionStorage.removeItem(
        STORAGE_KEY
      );

      window.location.reload();

    } catch (error) {
      console.error(
        'IMM: add-on processing error',
        error
      );
    }
  }

  function getBookingService(row) {
    return (
      Object.keys(SERVICES)
        .find(serviceKey =>
          rowMatchesService(
            row,
            serviceKey
          )
        ) ||
      null
    );
  }

  function getAddonMeta(row) {
    const title =
      getRowTitle(row);

    for (
      const [
        serviceKey,
        service
      ] of Object.entries(SERVICES)
    ) {
      for (
        const [
          addonKey,
          addon
        ] of Object.entries(
          service.addons
        )
      ) {
        if (
          title === addon.cartTitle
        ) {
          return {
            service: serviceKey,
            addonKey,
            order: addon.order,
            max: addon.max
          };
        }
      }
    }

    return null;
  }
  function getCartLimitMeta(row) {
    const addonMeta =
      getAddonMeta(row);

    if (addonMeta) {
      return addonMeta;
    }

    const bookingService =
      getBookingService(row);

    if (bookingService) {
      return {
        service: bookingService,
        max: 1
      };
    }

    const cartOnlyLimit =
      CART_ONLY_BOOKING_LIMITS[
        getRowTitle(row)
      ];

    if (cartOnlyLimit) {
      return cartOnlyLimit;
    }

    return null;
  }

  function getAddonItemMeta(item) {
    const title =
      normalizeTitle(
        item?.product_title
      );

    for (
      const [
        serviceKey,
        service
      ] of Object.entries(SERVICES)
    ) {
      for (
        const [
          addonKey,
          addon
        ] of Object.entries(
          service.addons
        )
      ) {
        if (
          title === addon.cartTitle
        ) {
          return {
            service: serviceKey,
            addonKey
          };
        }
      }
    }

    return null;
  }

  async function reconcileOrphanAddons() {
    if (
      window.location.pathname !== '/cart' ||
      orphanCleanupRunning
    ) {
      return;
    }

    orphanCleanupRunning = true;

    try {
      const response =
        await fetch(
          '/cart.js',
          {
            cache: 'no-store'
          }
        );

      if (!response.ok) {
        throw new Error(
          `Cart read failed: ${response.status}`
        );
      }

      const cart =
        await response.json();

      const orphanKeys = [];

      cart.items.forEach(item => {
        const addonMeta =
          getAddonItemMeta(item);

        if (!addonMeta) return;

        const addonDate =
          item.properties?.[
            'Add-on for'
          ];

        const parentUuid =
          item.properties?._imm_parent_uuid;

        const hasParent =
          cart.items.some(candidate => {
            if (
              !itemMatchesService(
                candidate,
                addonMeta.service
              )
            ) {
              return false;
            }

            if (
              parentUuid &&
              candidate.properties?._addons_uuid
            ) {
              return (
                candidate.properties._addons_uuid ===
                parentUuid
              );
            }

            return (
              addonDate &&
              candidate.properties?.Date ===
                addonDate
            );
          });

        if (
          !hasParent &&
          item.key
        ) {
          orphanKeys.push(
            item.key
          );
        }
      });

      if (!orphanKeys.length) {
        return;
      }

      const updates =
        Object.fromEntries(
          orphanKeys.map(key => [
            key,
            0
          ])
        );

      const removeResponse =
        await fetch(
          '/cart/update.js',
          {
            method: 'POST',

            headers: {
              'Content-Type':
                'application/json',

              'Accept':
                'application/json'
            },

            body:
              JSON.stringify({
                updates
              })
          }
        );

      if (!removeResponse.ok) {
        throw new Error(
          `Orphan cleanup failed: ${removeResponse.status}`
        );
      }

      window.location.reload();

    } catch (error) {
      console.error(
        'IMM: orphan add-on cleanup error',
        error
      );

    } finally {
      orphanCleanupRunning = false;
    }
  }

  function installOrphanAddonWatcher() {
    if (
      window.location.pathname !== '/cart'
    ) {
      return;
    }

    const cartItems =
      document.querySelector(
        '#main-cart-items'
      );

    if (!cartItems) {
      return;
    }

    let timer = null;

    const scheduleCleanup = () => {
      clearTimeout(timer);

      timer =
        setTimeout(
          reconcileOrphanAddons,
          350
        );
    };

    const observer =
      new MutationObserver(
        scheduleCleanup
      );

    observer.observe(
      cartItems,
      {
        childList: true,
        subtree: true
      }
    );

    reconcileOrphanAddons();
  }

  function enforceCartLimits() {
    if (
      window.location.pathname !== '/cart'
    ) {
      return;
    }

    const rows = [
      ...document.querySelectorAll(
        '#main-cart-items .cart-item'
      )
    ];

    rows.forEach(row => {
      const meta =
  getCartLimitMeta(row);

      if (!meta) return;

      const input =
        row.querySelector(
          '.quantity__input'
        );

      const plus =
        row.querySelector(
          'button[name="plus"]'
        );

      if (
        !input ||
        !plus
      ) {
        return;
      }

      input.max =
        String(meta.max);

      const qty =
        Number(input.value) ||
        0;

      plus.disabled =
        qty >= meta.max;

      plus.setAttribute(
        'aria-disabled',
        qty >= meta.max
          ? 'true'
          : 'false'
      );
    });
  }

  function installCartLimitGuards() {
    if (
      window.location.pathname !== '/cart'
    ) {
      return;
    }

    document.addEventListener(
      'click',
      event => {
        const plus =
          event.target.closest(
            'button[name="plus"]'
          );

        if (!plus) return;

        const row =
          plus.closest(
            '.cart-item'
          );

        const meta =
          row
            ? getCartLimitMeta(row)
            : null;

        if (!meta) return;

        const input =
          row.querySelector(
            '.quantity__input'
          );

        if (!input) return;

        if (
          (Number(input.value) || 0) >=
          meta.max
        ) {
          event.preventDefault();
          event.stopImmediatePropagation();

          enforceCartLimits();
        }
      },
      true
    );

    // Do not rewrite quantity values on the 'change' event.
    // Dawn 15.4 validates against input.max itself. Rewriting
    // here makes Dawn think an invalid typed value is valid,
    // causing an unnecessary cart/change request and re-render.
  }

  setInterval(
    installBookingAddons,
    500
  );

  setInterval(
    enforceCartLimits,
    500
  );

  installCartLimitGuards();
  installOrphanAddonWatcher();
  processPendingAddons();
})();